import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { Seo } from "./components/Seo";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Services } from "./components/Services";
import { CTABand } from "./components/CTABand";
import { Differentials } from "./components/Differentials";
import { TaxSummary } from "./components/TaxSummary";
import { ReformaTributaria } from "./components/ReformaTributaria";
import { AgendaTributaria } from "./components/AgendaTributaria";
import { NewsSection } from "./components/NewsSection";
import { LocationSection } from "./components/LocationSection";
import { ContactSection } from "./components/ContactSection";
import { MetricasPage } from "./components/MetricasPage";
import { Footer } from "./components/Footer";
import { ChatWidget } from "./components/ChatWidget";
import { track } from "./analytics";

const HomePage = () => (
    <main>
        <Seo
            title="WBS Serviços Contábeis | Contabilidade em SP e todo o Brasil"
            description="Contabilidade para MEI, Simples Nacional, Lucro Presumido e Lucro Real. Atendimento em SP e online para todo o Brasil. Peça seu orçamento."
        />
        <Hero />
        <CTABand />
        <Differentials />
        <LocationSection />
    </main>
);

const ServicosPage = () => (
    <main>
        <Seo
            title="Serviços Contábeis: MEI, Simples, Presumido e Real | WBS"
            description="Contabilidade completa por regime tributário, BPO Financeiro, ERP de gestão, regularização de empresas e recuperação de créditos fiscais."
        />
        <Services />
        <CTABand />
    </main>
);

const TributacaoPage = () => (
    <main>
        <Seo
            title="Tributação e Agenda Fiscal por Regime | WBS Contábil"
            description="Resumo da tributação e das obrigações do MEI, Simples Nacional, Lucro Presumido e Lucro Real, com a agenda de prazos fiscais."
        />
        <TaxSummary />
        <AgendaTributaria />
    </main>
);

const ReformaPage = () => (
    <main>
        <Seo
            title="Reforma Tributária: o que muda para sua empresa | WBS"
            description="Entenda o IVA dual (IBS e CBS), o Imposto Seletivo e a linha do tempo da transição da Reforma Tributária de 2026 a 2033."
        />
        <ReformaTributaria />
    </main>
);

const NoticiasPage = () => (
    <main>
        <Seo
            title="Notícias Tributárias em Tempo Real | WBS Contábil"
            description="Acompanhe as mudanças tributárias e as novidades fiscais que impactam a sua empresa, direto dos principais portais contábeis."
        />
        <NewsSection />
    </main>
);

const ContatoPage = () => (
    <main>
        <Seo
            title="Fale com um Contador | WBS Serviços Contábeis"
            description="Peça um orçamento ou agende um diagnóstico gratuito. Atendimento por WhatsApp, telefone e e-mail, em SP e online para o Brasil."
        />
        <ContactSection />
        <LocationSection />
    </main>
);

const ScrollProgress = () => {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const onScroll = () => {
            const el = document.documentElement;
            const max = el.scrollHeight - el.clientHeight;
            setWidth(max > 0 ? (el.scrollTop / max) * 100 : 0);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return (
        <div
            data-testid="scroll-progress"
            aria-hidden="true"
            className="fixed top-0 left-0 h-[3px] z-[60] bg-gradient-to-r from-amber-600 via-amber-400 to-amber-300"
            style={{ width: `${width}%` }}
        />
    );
};

const AnalyticsTracker = () => {
    const location = useLocation();
    useEffect(() => {
        track("page_view", { path: location.pathname });
        window.scrollTo({ top: 0 });
    }, [location.pathname]);
    useEffect(() => {
        const timer = setInterval(() => {
            track("heartbeat", { seconds: 30, path: window.location.pathname });
        }, 30000);
        return () => clearInterval(timer);
    }, []);
    return null;
};

function App() {
    return (
        <div className="App">
            <ScrollProgress />
            <HelmetProvider>
                <BrowserRouter>
                    <AnalyticsTracker />
                    <Navbar />
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/servicos" element={<ServicosPage />} />
                        <Route path="/tributacao" element={<TributacaoPage />} />
                        <Route path="/reforma" element={<ReformaPage />} />
                        <Route path="/noticias" element={<NoticiasPage />} />
                        <Route path="/contato" element={<ContatoPage />} />
                        <Route path="/metricas" element={<MetricasPage />} />
                    </Routes>
                    <Footer />
                </BrowserRouter>
            </HelmetProvider>
            <Toaster position="top-center" richColors />
            <ChatWidget />
        </div>
    );
}

export default App;
